import React from 'react';
import Card from '../../atoms/Card';
import Tag from '../../atoms/Tag';
import Link from 'next/link';
import { capitalizeText } from '../../../helpers/utils';
import eventClickNews from 'ui/lib/gtm/eventClickNews';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import { useRouter } from '../../../hooks/useCustomRouter';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import eventClickPromoHomePage from '../../../lib/gtm/eventClickPromoHomePage';
import eventClickHomepage from '../../../helpers/emarsys/homepage/eventClickHomepage';
import eventClickProductKnowledgeList from '../../../helpers/emarsys/product-knowledge/eventClickProductKnowledgeList';
import eventClickNewsList from '../../../helpers/emarsys/news/eventClickNewsList';

interface ICardNewsProps {
  slug: string;
  title: string;
  image: string;
  altImage: string;
  date: string;
  tag: string | null;
  category: string | string[];
  AdditionalClassName?: string;
  idArticle: string | number;
  categoryArticle: string;
  indexArticle: number;
  handleCollectDataLayer: (data: {
    id?: string | number;
    name?: string;
    creative?: string;
    position?: string;
    category?: string;
  }) => void;
  rootMargin: string;
  href?: string;
  isHomepage?: boolean;
  as?: keyof JSX.IntrinsicElements;
  feature?: string;
  emarsysData?: {
    pageTitle: string;
    category: string;
    page_number: number;
  };
}

const CardNews = ({
  category,
  AdditionalClassName,
  rootMargin,
  href = null,
  altImage = '',
  title = '',
  slug = '',
  image = '',
  date = '',
  tag = null,
  idArticle = '',
  categoryArticle = '',
  indexArticle = 0,
  isHomepage = false,
  as: CustomTag = 'p',
  feature = '',
  emarsysData,
  handleCollectDataLayer = () => {},
}: ICardNewsProps) => {
  const PUBLIC_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;
  const router = useRouter();
  const pageCurrent = router?.query?.page ? parseInt(router?.query?.page as string) : 1;
  return (
    <React.Fragment>
      <Link
        ref={useIsElementVisible(
          () =>
            handleCollectDataLayer({
              id: idArticle,
              name: title?.toLowerCase() || 'Tidak Tersedia',
              creative: `${router.basePath}/${slug?.toLowerCase()}`,
              position: `${indexArticle + 2 + (pageCurrent - 1) * 9} - ${
                router.basePath === '/berita-dan-tips'
                  ? 'berita & tips'
                  : router.basePath?.slice(1)?.replace(/-/g, ' ')
              } ${categoryArticle?.toLowerCase()?.replace(/-/g, ' ')}`,
              category: (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
                ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
                : 'semua',
            }),
          [category, pageCurrent],
          null,
          false,
          rootMargin
        )}
        id={`card-news-${slug}`}
        href={href}
        onClick={() => {
          if (feature === 'LISTING_PRODUCT_KNOWLEDGE') {
            eventClickProductKnowledgeList(
              emarsysData?.pageTitle,
              emarsysData?.category,
              emarsysData?.page_number,
              'list review',
              'lihat detail',
              title
            );
          }
          if (feature === 'NEWS_LIST') {
            eventClickNewsList(
              emarsysData?.pageTitle,
              emarsysData?.category,
              emarsysData?.page_number,
              'list berita',
              'lihat detail',
              title
            );
          }
          if (isHomepage) {
            eventClickPromoHomePage(
              {
                id: `${idArticle || 'not available'}`,
                name: `${title?.toLowerCase() || 'not available'}`,
                creative: `${PUBLIC_URL}/berita-dan-tips/${slug?.toLowerCase()}`,
                position: `${indexArticle + 1} - berita & tips ${categoryArticle?.toLowerCase()?.replace(/-/g, ' ')}`,
              },
              'berita & tips'
            );
            eventClickHomepage('homepage auto2000', 'berita & tips', 'pilih berita & tips ', {
              beritaTipsName: title,
            });
          } else {
            eventClickNews({
              baseSlug: router.basePath,
              id: idArticle,
              title: title,
              category: categoryArticle,
              slug: slug,
              index: indexArticle + 2 + (pageCurrent - 1) * 9,
              anotherCategory: (router?.query?.kategori as string)
                ?.toLowerCase()
                ?.replace(/-/g, ' ')
                ? (router?.query?.kategori as string)?.toLowerCase()?.replace(/-/g, ' ')
                : 'semua',
            });
          }
        }}
      >
        <Card className={`border-b-[1px] border-[#E9E9E9] ${AdditionalClassName}`}>
          {/* Tag */}
          <div className={`${!tag && 'pb-0'}`}>
            {category === '' && tag ? (
              <Tag
                className="rounded-br h-[23px] md:h-[28px] text-xs"
                severity={`${category === '' ? 'warn' : 'info'}`}
              >
                {capitalizeText(tag)}
              </Tag>
            ) : null}
          </div>
          <div
            className={`flex justify-start md:gap-3 gap-[10px] ${tag ? 'pt-2' : 'pt-4'} px-4 pb-4 ${isHomepage ? '!px-0' : ''}`}
          >
            <img
              className="w-[124px] h-[70px] md:w-[177.77px] md:h-[100px] object-cover aspect-video"
              src={generateImageResizer(image, 240, 'webp')}
              alt={altImage}
              decoding="async"
              loading="lazy"
              fetchPriority="low"
            />
            <div className="text-ellipsis flex flex-col overflow-hidden">
              <CustomTag
                className={`${isHomepage ? 'text-supportiveGray text-xs' : 'text-reliableBlack text-sm'} font-semibold mb-1 line-clamp-2 md:text-base`}
              >
                {title}
              </CustomTag>

              <p
                className={`${isHomepage ? 'text-reliableBlack50' : 'text-[#333]'} text-xs font-normal lg:text-sm`}
              >
                {date}
              </p>
            </div>
          </div>
        </Card>
      </Link>
    </React.Fragment>
  );
};

export default CardNews;
